import API from '../../api';
import Endpoints from '../../endpoints';
import { QueryResult, useQuery } from 'react-query';
import {
  PlanInfoOptions, RecommendedSizeOptions, defaultPlanResponse, defaultRecommendedSize,
  ClosetMinOptions, ClosetMinResponse, ClosetSummaryOptions, ClosetSummaryResponse,
  PlanAndClosetInfoOptions, PlanAndClosetInfoResponse, PlanInfoResponse,
  RecommendedClosetSizeResponse, defaultSummaryResponse, DeliveryTimeResponse, DeliveryTimeResponseOptions
} from '../../types/common';
import { useContext } from 'react';
import GlobalContext from '../../contexts/GlobalContext';

export const usePlanInfo = (
  options: PlanInfoOptions
): QueryResult<PlanInfoResponse> => {
  const endpoints = Endpoints();
  const fetchPlanInfo = async (): Promise<PlanInfoResponse> => {
    try {
      let planInfodata: PlanInfoResponse = defaultPlanResponse;
      let queryParams = [
        'sourceEventId=' + options.sourceEventId,
        'cluster=' + options.clusterValue
      ].join('&');
      const url = endpoints.itemPlans() + '?' + queryParams;
      const response = await API.get(url);
      const { status, data } = response;

      planInfodata.status = data.status.code;

      if (status === 200 && data.status && data.status.code === 0) {
        planInfodata.numItems = data.plans[0].numItems;
        planInfodata.addOns = data.plans[0].addOns;
      } else {
        planInfodata.invalidResponse = true;
      }

      return planInfodata;
    } catch (error) {
      throw error;
    }
  };

  return useQuery('plan-info', fetchPlanInfo, {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled,
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useRecommendedClosetSizeInfo = (
  options: RecommendedSizeOptions
): QueryResult<RecommendedClosetSizeResponse> => {
  const endpoints = Endpoints();
  const fetchRecommendedClosetSizeInfo = async (): Promise<RecommendedClosetSizeResponse> => {
    try {
      let recommendedSize: RecommendedClosetSizeResponse = defaultRecommendedSize;
      const response = await API.get(endpoints.recommendedClosetSize());
      const { status, data } = response;

      recommendedSize.status = data.status.code;

      if (status === 200 && data.status && data.status.code === 0) {
        recommendedSize.recommendedClosetSize = data.recommendedClosetSizes[0].recommendedClosetSize;
      } else {
        recommendedSize.invalidResponse = true;
      }

      return recommendedSize;
    } catch (error) {
      throw error;
    }
  };

  return useQuery('recommendedClosetSize-info', fetchRecommendedClosetSizeInfo, {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled,
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useClosetSummary = (options: ClosetSummaryOptions): QueryResult<ClosetSummaryResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const endpoints = Endpoints();
  let summaryUrl = endpoints.summary();
  let queryParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue()
  ].join('&');
  const url = summaryUrl + '?' + queryParams;
  const fetchClosetSummary = async function (): Promise<ClosetSummaryResponse> {
    try {
      let summaryResponse: ClosetSummaryResponse = defaultSummaryResponse;
      const response = await API.get(url);
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code === 0) {
        summaryResponse.response = data.summary;
      } else {
        summaryResponse.invalidResponse = true;
      }

      return summaryResponse;
    } catch (error) {
      throw error;
    }
  };

  return useQuery('fetchclosetSummary', fetchClosetSummary, {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    enabled: options.enabled
  });
};

// this hook combines usePlanInfo, useClosetSummary, useRecommendedClosetSizeInfo hooks.
export const useFetchPlanAndClosetInfo = (
  options: PlanAndClosetInfoOptions
): QueryResult<PlanAndClosetInfoResponse> => {
  const endpoints = Endpoints();
  const sourceEventId = options.sourceEventId;
  const clusterValue = options.clusterValue;
  const fetchPlanInfo = async (): Promise<PlanInfoResponse> => {
    try {
      let planInfodata: PlanInfoResponse;
      const response = await API.get(endpoints.itemPlans());
      const { status, data } = response;

      if (status === 200 && data?.status?.code === 0) {
        planInfodata = {
          numItems: data?.plans?.length > 0 && data?.plans[0]?.numItems
        };
      } else {
        planInfodata = {
          invalidResponse: true
        };
      }

      return planInfodata;
    } catch (error) {
      throw error;
    }
  };
  const fetchclosetSummary = async function (): Promise<ClosetSummaryResponse> {
    try {
      let summaryResponseData: ClosetSummaryResponse;
      let queryParams = [
        'sourceEventId=' + sourceEventId,
        'cluster=' + clusterValue
      ].join('&');
      let summaryUrl = endpoints.summary();
      const url = summaryUrl + '?' + queryParams;
      const response = await API.get(url);
      const { status, data } = response;

      if (status === 200 && data?.status?.code === 0 && data?.summary) {
        summaryResponseData = {
          response: data.summary
        };
      } else {
        summaryResponseData = {
          invalidResponse: true
        };
      }

      return summaryResponseData;
    } catch (error) {
      throw error;
    }
  };
  const fetchRecommendedClosetSizeInfo = async (): Promise<RecommendedClosetSizeResponse> => {
    try {
      let recommendedSizeData: RecommendedClosetSizeResponse;
      const response = await API.get(endpoints.recommendedClosetSize());
      const { status, data } = response;

      if (status === 200 && data?.status?.code === 0) {
        recommendedSizeData = {
          recommendedClosetSize:
              data?.recommendedClosetSizes?.length > 0 &&
              data?.recommendedClosetSizes[0]?.recommendedClosetSize
        };
      } else {
        recommendedSizeData = {
          invalidResponse: true
        };
      }

      return recommendedSizeData;
    } catch (error) {
      throw error;
    }
  };
  const fetchPlanInfoPromise = fetchPlanInfo();
  const fetchclosetSummaryPromise = fetchclosetSummary();
  const fetchRecommendedSizeInfoPromise = fetchRecommendedClosetSizeInfo();

  return useQuery('fetchPlanAndClosetInfo', async () => {
    try {
      let planAndClosetData: PlanAndClosetInfoResponse;
      const combinedData = await Promise.all([
        fetchPlanInfoPromise,
        fetchclosetSummaryPromise,
        fetchRecommendedSizeInfoPromise
      ]);

      planAndClosetData = {
        planInfo: combinedData[0],
        closetSummaryInfo: combinedData[1],
        recommendedClosetSizeInfo: combinedData[2]
      };

      return planAndClosetData;
    } catch (error) {
      throw error;
    }
  }, {
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    enabled: options.enabled
  });
};

export const useClosetMin = (options: ClosetMinOptions): QueryResult<ClosetMinResponse> => {
  const { globalContext } = useContext(GlobalContext);
  const endpoints = Endpoints();
  let closetMinUrl = options.isSubscriber ? endpoints.closetMinSubscriberUrl() : endpoints.closetMinNonSubscriberUrl();
  let queryParams = [
    'sourceEventId=' + globalContext.emitter.sourceEventId,
    'cluster=' + globalContext.gb.utils.getClusterValue()
  ].join('&');
  const url = closetMinUrl + '?' + queryParams;
  const fetchClosetMin = async function (): Promise<ClosetMinResponse> {
    try {
      let closetMin: ClosetMinResponse = {
        closetMinResponse: {
          uuid: '',
          planGarments: 0,
          addOnGarments: 0,
          openSlots: 0,
          currentClosetSize: 0,
          closetMinimumSize: 0
        },
        status: {
          code: 1,
          message: '',
          sfUserMessage: ''
        }
      };
      const response = await API.get(url);
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code === 0) {
        closetMin.closetMinResponse = data.closetMin;
      } else {
        closetMin.invalidResponse = true;
      }

      return closetMin;
    } catch (error) {
      throw error;
    }
  };

  return useQuery('fetchclosetMin', fetchClosetMin, {
    retry: false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    enabled: options.enabled
  });
};

export const useDeliveryTime = (options: DeliveryTimeResponseOptions): QueryResult<DeliveryTimeResponse> => {
  const endpoints = Endpoints();
  const configUrl = endpoints.expectedShipmentDeliveryDayUrl();

  const fetchDeliveryTime = async (): Promise<DeliveryTimeResponse> => {
    try {
      let deliveryTimeResponse: DeliveryTimeResponse = { shipmentTime : 0};
      const response = await API.get(configUrl, {
        headers: { 'Content-Type': 'application/json' },
      });
      const { status, data } = response;

      if (status === 200 && data.status && data.status.code === 0) {
        deliveryTimeResponse.shipmentTime = data.shipmentTime;
      } else {
        deliveryTimeResponse.invalidResponse = true;
      }

      return deliveryTimeResponse;
    } catch (err) {
      throw err;
    }
  };

  return useQuery('fetchDeliveryTime', fetchDeliveryTime, {
    retry: false, 
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    onError: options.onError,
    enabled: options.enabled
  });
};